import { useEffect, useMemo, useState } from "react";
import {

    FiPieChart,
    FiSave,
    FiX,
    FiXCircle,
} from "react-icons/fi";

const API_URL = process.env.REACT_APP_API_URL || "";

const initialState = {
    anio: new Date().getFullYear(),
    mes: new Date().getMonth() + 1,
    idCategoria: "",
    idSubcategoria: "",
    montoAsignado: "",
};

const meses = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
];

export default function PresupuestoForm({ initialData, onClose, onSave }) {
    const [form, setForm] = useState(initialState);
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);
    const [loadingCatalogos, setLoadingCatalogos] = useState(false);

    useEffect(() => {
        if (initialData) {
            setForm({
                ...initialState,
                ...initialData,
                idCategoria:
                    initialData.idCategoria ??
                    initialData.id_categoria ??
                    "",
                idSubcategoria:
                    initialData.idSubcategoria ??
                    initialData.id_subcategoria ??
                    "",
                montoAsignado:
                    initialData.montoAsignado ??
                    initialData.monto_asignado ??
                    initialData.asignado ??
                    "",
            });
        } else {
            setForm(initialState);
        }
    }, [initialData]);

    useEffect(() => {
        cargarCatalogos();
    }, []);

    const cargarCatalogos = async () => {
        try {
            setLoadingCatalogos(true);

            const [resCategorias, resSubcategorias] = await Promise.all([
                fetch(`${API_URL}/api/catalogos/categorias`),
                fetch(`${API_URL}/api/catalogos/subcategorias`),
            ]);

            const dataCategorias = resCategorias.ok ? await resCategorias.json() : [];
            const dataSubcategorias = resSubcategorias.ok ? await resSubcategorias.json() : [];

            setCategorias(Array.isArray(dataCategorias) ? dataCategorias : []);
            setSubcategorias(Array.isArray(dataSubcategorias) ? dataSubcategorias : []);
        } catch (error) {
            console.error("Error cargando catálogos:", error);
            setCategorias([]);
            setSubcategorias([]);
        } finally {
            setLoadingCatalogos(false);
        }
    };

    const subcategoriasFiltradas = useMemo(() => {
        const idCategoriaNum = Number(form.idCategoria);
        if (!idCategoriaNum) return [];

        return subcategorias.filter(
            (item) =>
                Number(item.id_categoria ?? item.idCategoria) === idCategoriaNum
        );
    }, [subcategorias, form.idCategoria]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => {
            const next = { ...prev, [name]: value };

            if (name === "idCategoria") {
                next.idSubcategoria = "";
            }

            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        await onSave({
            ...form,
            anio: Number(form.anio),
            mes: Number(form.mes),
            idCategoria: Number(form.idCategoria || 0),
            idSubcategoria: form.idSubcategoria ? Number(form.idSubcategoria) : null,
            montoAsignado: Number(form.montoAsignado || 0),
        });
    };

    return (
        <div className="modal-backdrop">
            <div className="modal-card">
                <div className="modal-header">
                    <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FiPieChart size={20} />
                        {initialData ? "Editar presupuesto" : "Nuevo presupuesto"}
                    </h3>

                    <button className="btn-secondary" type="button" onClick={onClose}>
                        <FiX size={16} />
                    </button>
                </div>

                <form className="grid-form" onSubmit={handleSubmit}>
                    <div>
                        <label>Año</label>
                        <input
                            name="anio"
                            type="number"
                            value={form.anio}
                            onChange={handleChange}
                        />
                    </div>

                    <div>
                        <label>Mes</label>
                        <select name="mes" value={form.mes} onChange={handleChange}>
                            {meses.map((mes) => (
                                <option key={mes.value} value={mes.value}>
                                    {mes.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label>Categoría</label>
                        <select
                            name="idCategoria"
                            value={form.idCategoria}
                            onChange={handleChange}
                            disabled={loadingCatalogos}
                        >
                            <option value="">Seleccione categoría</option>
                            {categorias.map((cat) => (
                                <option
                                    key={cat.id_categoria ?? cat.idCategoria}
                                    value={cat.id_categoria ?? cat.idCategoria}
                                >
                                    {cat.nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label>Subcategoría</label>
                        <select
                            name="idSubcategoria"
                            value={form.idSubcategoria}
                            onChange={handleChange}
                            disabled={!form.idCategoria || loadingCatalogos}
                        >
                            <option value="">Seleccione subcategoría</option>
                            {subcategoriasFiltradas.map((sub) => (
                                <option
                                    key={sub.id_subcategoria ?? sub.idSubcategoria}
                                    value={sub.id_subcategoria ?? sub.idSubcategoria}
                                >
                                    {sub.nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label>Monto asignado</label>
                        <input
                            name="montoAsignado"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={form.montoAsignado}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-actions full-width">
                        <button className="btn-secondary" type="button" onClick={onClose}>
                            <FiXCircle size={16} />
                            Cancelar
                        </button>

                        <button className="btn-primary" type="submit">
                            <FiSave size={16} />
                            Guardar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}